import styled from "styled-components"

/* ============================================================
   Design tokens (locais) — coerentes com Payment/styled.ts
   ============================================================ */
const tokens = {
  surface: "#FFFFFF",
  surfaceMuted: "#F8FAFC",
  borderSubtle: "rgba(16, 24, 40, 0.08)",
  borderDefault: "#E2E8F0",
  textPrimary: "#0F172A",
  textSecondary: "#475569",
  textMuted: "#64748B",
  shadowSoft:
    "0 1px 2px rgba(16, 24, 40, 0.04), 0 8px 24px rgba(16, 24, 40, 0.06)",
  radiusCard: "16px",
}

export const Component = styled.div<{
  $fitContainer: boolean
  $reverse: boolean
}>`
  flex: 2;
  display: flex;
  flex-direction: ${({ $reverse }) => ($reverse ? "column-reverse" : "column")};
  background: ${tokens.surface};
  border: 1px solid ${tokens.borderSubtle};
  border-radius: ${tokens.radiusCard};
  overflow: hidden;
  box-shadow: ${tokens.shadowSoft};
  ${({ $fitContainer }) =>
    $fitContainer ? "align-self: stretch;" : "height: fit-content;"}
  min-width: 340px;

  @media (max-width: ${({ theme }) => theme.bp.small}px) {
    min-width: unset;
  }

  opacity: 0;
  ${({ theme }) =>
    theme.animations.types.fade +
    theme.animations.durations.main +
    theme.animations.delays.main(0)}
`

export const ImageContainer = styled.div`
  width: 100%;
  aspect-ratio: 2.4 / 1;
  overflow: hidden;
  display: flex;
  align-items: center;
  background: ${tokens.surfaceMuted};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media (max-width: ${({ theme }) => theme.bp.small}px) {
    aspect-ratio: 2.2 / 1;
  }

  opacity: 0;
  ${({ theme }) =>
    theme.animations.types.fade +
    theme.animations.durations.main +
    theme.animations.delays.main()}
`

export const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;

  @media (max-width: ${({ theme }) => theme.bp.small}px) {
    padding: 20px 18px;
    gap: 16px;
  }
`

export const InfoWrapper = styled.div<{ $expanded: boolean }>`
  display: grid;
  grid-template-rows: ${({ $expanded }) => ($expanded ? "1fr" : "0fr")};
  overflow: hidden;
  transition: grid-template-rows 260ms ease-out;

  & > div {
    min-height: 0;
  }

  @media (min-width: ${({ theme }) => theme.bp.small + 1}px) {
    grid-template-rows: 1fr;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const InfoData = styled.div`
  min-height: 0;
`

export const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

/* Header do resumo (sempre visível) — título + toggle no mobile */
export const ResumeHeader = styled.div<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  svg {
    transition: transform 240ms ease-out;
    transform: rotate(${({ $expanded }) => ($expanded ? -180 : 0)}deg);
    display: none;
    color: ${tokens.textMuted};
    flex-shrink: 0;
  }

  @media (max-width: ${({ theme }) => theme.bp.small}px) {
    cursor: pointer;
    user-select: none;

    svg {
      display: block;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    svg {
      transition: none;
    }
  }
`

export const DateBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

/* Alias legado — mantido para compat de imports antigos */
export const EventResume = styled.div``

export const ResumeText = styled.h3`
  font-weight: 700;
  font-size: 18px;
  color: ${tokens.textPrimary};
  margin: 0;
  letter-spacing: -0.01em;
  line-height: 1.2;

  opacity: 0;
  ${({ theme }) =>
    theme.animations.types.fadeLeft +
    theme.animations.durations.main +
    theme.animations.delays.main(2)}
`

export const DateLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${tokens.textMuted};
  line-height: 1.2;

  opacity: 0;
  ${({ theme }) =>
    theme.animations.types.fade +
    theme.animations.durations.main +
    theme.animations.delays.main(3)}
`

export const DateValue = styled.span`
  font-size: 14.5px;
  font-weight: 600;
  color: ${tokens.textPrimary};
  text-transform: uppercase;
  line-height: 1.35;
  letter-spacing: 0.01em;
  font-variant-numeric: tabular-nums;
  word-break: keep-all;
  overflow-wrap: normal;

  opacity: 0;
  ${({ theme }) =>
    theme.animations.types.fade +
    theme.animations.durations.main +
    theme.animations.delays.main(4)}
`

/* Lista tabular de itens */
export const ItemsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 4px;
  border-top: 1px solid ${tokens.borderSubtle};
  padding-top: 16px;
`

export const ItemsHead = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 16px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${tokens.textMuted};
  line-height: 1.2;
  padding: 0 2px 4px;

  & > span:nth-child(2) {
    text-align: center;
    min-width: 28px;
  }

  & > span:nth-child(3) {
    text-align: right;
    min-width: 72px;
  }
`

export const ItemRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 16px;
  align-items: baseline;
  padding: 8px 2px;
  font-size: 14.5px;
  color: ${tokens.textPrimary};

  & + & {
    border-top: 1px dashed ${tokens.borderSubtle};
  }

  @media (max-width: ${({ theme }) => theme.bp.small}px) {
    gap: 12px;
    font-size: 14px;
  }
`

export const ItemName = styled.span`
  font-weight: 600;
  color: ${tokens.textPrimary};
  overflow-wrap: anywhere;
`

export const ItemQty = styled.span`
  font-weight: 600;
  color: ${tokens.textSecondary};
  text-align: center;
  min-width: 28px;
  font-variant-numeric: tabular-nums;
`

export const ItemSubtotal = styled.span`
  font-weight: 600;
  color: ${tokens.textPrimary};
  text-align: right;
  min-width: 72px;
  font-variant-numeric: tabular-nums;
`

/* Totais */
export const Total = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid ${tokens.borderSubtle};
`

export const TotalItem = styled.div<{ $main?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  font-weight: ${({ $main }) => ($main ? 800 : 500)};
  font-size: ${({ $main }) => ($main ? "22px" : "14.5px")};
  color: ${({ $main }) => ($main ? tokens.textPrimary : tokens.textSecondary)};
  letter-spacing: ${({ $main }) => ($main ? "-0.01em" : "0")};
  line-height: 1.2;

  ${({ $main }) =>
    $main &&
    `
      padding-top: 6px;
      margin-top: 2px;
    `}

  & > span:first-child {
    ${({ $main }) =>
      $main &&
      `
      text-transform: uppercase;
      letter-spacing: 0.05em;
      font-size: 13px;
      font-weight: 700;
    `}
  }

  & > span:last-child {
    font-variant-numeric: tabular-nums;
  }

  opacity: 0;
  ${({ theme }) =>
    theme.animations.types.fadeTop + theme.animations.durations.main}

  &:nth-child(1) {
    ${({ theme }) => theme.animations.delays.main(5)}
  }

  &:nth-child(2) {
    ${({ theme }) => theme.animations.delays.main(6)}
  }

  &:nth-child(3) {
    ${({ theme }) => theme.animations.delays.main(7)}
  }
`

/* Legado não utilizado — mantido para compat de imports externos */
export const TicketsList = styled.div`
  display: none;
`

export const ReleaseBlock = styled.div`
  display: flex;
  background-color: #222;
  padding: 24px;
  color: #fff;
  gap: 24px;

  @media (max-width: 380px) {
    flex-direction: column;
  }
`

export const RLeft = styled.div`
  flex: 2;
  display: flex;
  align-items: center;
  gap: 12px;

  img {
    width: 24px;
  }

  span {
    font-size: 24px;
    font-weight: bold;
  }
`

export const RRight = styled.div`
  flex: 3;
  font-size: 14px;
  padding-left: 24px;
  border-left: 4px solid #888;
`

/* Aliases legados para compat com eventuais imports antigos */
export const DateText = DateValue
